import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AiChatLine,
  FileTemplateLine,
  SliderLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo, useCallback } from "react";
import styles from "./AIToolLibraryMobileTabs.module.css";
import type { AIToolLibraryTab } from "./AIToolLibraryModal";

interface AIToolLibraryMobileTabsProps {
  activeTab: AIToolLibraryTab;
  onSwitchTab: (tab: AIToolLibraryTab) => void;
}

export const AIToolLibraryMobileTabs = memo(
  ({ activeTab, onSwitchTab }: AIToolLibraryMobileTabsProps) => {
    const promptsEnabled = useFeatureFlagValueBool(
      "dash_2025_06_25_assist_ai_tool_library_prompts",
    );

    const handleTabClick = useCallback(
      (tab: AIToolLibraryTab) => () => {
        onSwitchTab(tab);
      },
      [onSwitchTab],
    );

    const tabs = [
      {
        id: "templates" as AIToolLibraryTab,
        label: i18n.t("ai_tool_library_templates_tab"),
        icon: FileTemplateLine,
      },
      {
        id: "tones" as AIToolLibraryTab,
        label: i18n.t("ai_tool_library_tones_tab"),
        icon: SliderLine,
      },
      ...(promptsEnabled
        ? [
            {
              id: "prompts" as AIToolLibraryTab,
              label: i18n.t("ai_tool_library_prompts"),
              icon: AiChatLine,
            },
          ]
        : []),
    ];

    return (
      <div className={styles.AIToolLibraryMobileTabs} role="tablist">
        {tabs.map(({ id, label, icon }) => (
          <div
            key={id}
            role="tab"
            aria-selected={activeTab === id}
            aria-labelledby={label}
            tabIndex={0}
            className={styles.AIToolLibraryMobileTabWrapper}
            onClick={handleTabClick(id)}
            onKeyDown={onKeyDownCommitFn(handleTabClick(id))}
          >
            <div
              className={classNames(styles.AIToolLibraryMobileTabUnderline, {
                [styles.AIToolLibraryMobileTabSelected]: id === activeTab,
              })}
            >
              <div className={styles.AIToolLibraryMobileTabContent}>
                <UIIcon src={icon} size="small" />
                <Text
                  size="medium"
                  isBold={activeTab === id}
                  color={activeTab === id ? "standard" : "faint"}
                >
                  {label}
                </Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  },
);

AIToolLibraryMobileTabs.displayName = "AIToolLibraryMobileTabs";
